import { Button } from '@mui/material';
import { useCart } from '../../../hooks/useCart';
import { FC, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import styles from './salesWindow.module.scss';
import { useModal } from '../../../hooks/useModal';
import ValuationsDialogForm from '../../../pages/PieceOfFurniture/FurnitureItem/components/ModulesComponent/components/ValuationsDialogForm/ValuationsDialogForm';


const AddIconButton = styled(Button) <{ showButton: boolean, isInCart: boolean }>`
    position: absolute;
    right: 15px;
    top: 15px;
    z-index: 10;
    border: 1px solid ${({ isInCart }) => isInCart ? 'transparent' : 'green'};   
    background-color: ${({ isInCart }) => isInCart ? 'none' : 'white'};
    display: ${({ showButton }) => showButton ? 'flex' : 'none'};
    transition: all 0.2s ease;

    &:hover {
        background-color: ${({ isInCart }) => isInCart ? '#ffa9a9' : '#a4ee90'};
        border: ${({ isInCart }) => isInCart ? '1px solid red' : ''};
    }

    @media screen and (max-width: 940px) {
        display: flex;
    }
`;

const ImageWrapper = styled('div')`
  position: relative;
  width: 100%;
  aspect-ratio: 1/1;
`;

const StyledImage = styled('img')`
  width: 100%;
  height: 100%;
  aspect-ratio: 1/1;
  object-fit: contain;
  padding-top: 5px;
  image-rendering: optimizeQuality;
  position: absolute;
  top: 0;
  left: 0;
`;

const Name = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 0 10px 0.5rem 10px;
  margin-top: 5px;
  font-weight: lighter;
  color: black;
`;

const Dimensions = styled('div')`
  font-size: 0.8rem;
  margin: 0 10px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-weight: lighter;
`;

const Dimension = styled('div')`
  color: gray;
`;

const DimensionValue = styled('div')`
  margin-left: 10px;
  color: black;
  font-weight: lighter;
`;

const Price = styled('div')`
  margin: 10px 30px;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  height: 100%;
  font-weight: lighter;
  font-size: 1.5rem;
  margin-top: 0.5rem;
  color: black;
  vertical-align: middle;
`;

const Crossed = styled('div')`
  margin-left: 10px;
  text-decoration: line-through 1px;
`;

interface SalesWindowProps {
    id: string,
    name: string,
    producer: string,
    width: number | null,
    depth: number | null,
    height: number | null,
    price?: number | null,
    crossed?: number | null,
    isPriceVissible: boolean,
    image: string,
    secondaryImage?: string,
    link?: string | null,
    type: 'furniture' | 'imported furniture',
    mode?: 'clarnet',
    isModule: boolean,
}

const renderDimension = (label: string, value: number | null) => (
    value ? (
        <Dimensions>
            <Dimension>{label}</Dimension>
            <DimensionValue>{value} cm</DimensionValue>
        </Dimensions>
    ) : (
        <Dimensions>
            <Dimension style={{ color: 'white' }} />
            <DimensionValue style={{ color: 'white' }}>cm</DimensionValue>
        </Dimensions>
    )
);

const SalesWindow: FC<SalesWindowProps> = ({
    id,
    name,
    producer,
    width,
    depth,
    height,
    price,
    crossed,
    isPriceVissible,
    image,
    secondaryImage,
    link,
    type,
    mode,
    isModule,
}) => {
    const modal = useModal()
    const cart = useCart();
    const [showCartButton, setShowCartButton] = useState(false)
    const [showSecondaryImage, setShowSecondaryImage] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null);

    const pageURL = window.location.origin;

    const onClickAddFurniture = () => {
        if (isModule) {
            return modal.open(
                <ValuationsDialogForm
                    furnitureId={id}
                    furnitureImage={`${image}`}
                    furnitureLink={`${pageURL}/mebel/${id}`}
                    furnitureName={name}
                    modules={null}
                />)
        }
        if (cart.isInCart(id)) {
            return cart.remove(id);
        }

        cart.add({
            type,
            name,
            id,
            image,
            link: `${pageURL}/${link}`,
            producer,
        });
    }

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerWidth < 940 && containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                if (rect.top < 90) {
                    setShowSecondaryImage(true);
                } else {
                    setShowSecondaryImage(false);
                }
            } else {
                setShowSecondaryImage(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div
            key={id}
            ref={containerRef}
            className={styles.container}
            onMouseEnter={() => { setShowCartButton(true); setShowSecondaryImage(true) }}
            onMouseLeave={() => { setShowCartButton(false); setShowSecondaryImage(false) }}
        >
            <div>
                <a
                    href={link ? `/${link}` : undefined}
                    target={link ? '_blank' : undefined}
                    rel="noopener noreferrer"
                >
                    <div style={{ position: 'relative' }}>
                        {
                            (mode !== 'clarnet' && !price) &&
                            <AddIconButton
                                isInCart={cart.isInCart(id)}
                                showButton={showCartButton || cart.isInCart(id)}
                                onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    onClickAddFurniture();
                                }}
                                color={cart.isInCart(id) ? 'primary' : 'success'}
                            >
                                {
                                    cart.isInCart(id) ?
                                        'W wycenach'
                                        :
                                        'Wyceń'
                                }
                            </AddIconButton>
                        }
                    </div>
                    <ImageWrapper>
                        <StyledImage
                            src={image}
                            alt={name}
                            loading="lazy"
                            style={{
                                opacity: secondaryImage ? (showSecondaryImage ? 0 : 1) : 1,
                                transition: secondaryImage ? 'opacity 0.4s' : 'none',
                                zIndex: 1,
                            }}
                        />
                        {secondaryImage && secondaryImage !== '' && (
                            <StyledImage
                                src={secondaryImage}
                                alt={name}
                                loading="lazy"
                                style={{
                                    opacity: showSecondaryImage ? 1 : 0,
                                    transition: 'opacity 0.4s',
                                    zIndex: 2,
                                    pointerEvents: 'none',
                                    display: secondaryImage ? 'block' : 'none',
                                }}
                                onError={e => (e.currentTarget.style.display = 'none')}
                            />
                        )}
                    </ImageWrapper>
                    <Name>{name}</Name>
                    {renderDimension('Szerokość:', width)}
                    {renderDimension('Głębokość:', depth)}
                    {renderDimension('Wysokość:', height)}
                </a>
                {isPriceVissible ? (
                    <Price>
                        {price}zł
                        <Crossed>{crossed ? `${crossed}zł` : ''}</Crossed>
                    </Price>
                ) : (
                    <Price />
                )}
            </div>
        </div>
    );
}

export default SalesWindow;